/**
 * Virtue Fusion Request Service
 */

define(function(require, exports, module) {
    'use strict';

    var $ = require('j');
    var _ = require('underscore');
    var server = require('server');
    var config = require('config');
    var authManager = require('auth');

    var currentToken = null;
    var requests = {};
    var TOKEN_TTL = 300000; // 5 min token TTL from Virtue Fusion documentation.
    var newVFRequestDelay = 0;

    /**
     * Help method to test token freshness.
     * @private
     * @return {boolean}
     * */
    var _isTokenFresh = function() {
        if (!currentToken || !currentToken.request) {
            currentToken = {
                request: $.Deferred()
            };
            return false;
        }

        var isFresh = currentToken.timestamp &&
            (new Date()).getTime() - currentToken.timestamp.getTime() <= TOKEN_TTL;

        return currentToken.request.state() === 'pending' || currentToken.request.state() === 'resolved' && isFresh ;
    };

    /**
     * Private method to get fresh Virtue Fusion token. Make request if current is rotten.
     * @private
     * @return {Deferred} - Virtue Fusion Token Deferred, with {string} .token attribute.
     * */
    var _getVirtueFusionToken = function() {
        var VFTokenRequestDelay = config.get('VFTokenRequestDelay') || 300000;

        var onSuccess = function(response) {
            currentToken.token = response.data.temporaryToken;
            currentToken.timestamp = new Date();
            currentToken.request.resolve(currentToken.token);
        };

        var onError = function(data) {
            currentToken.forceUpdate = true;

            setTimeout(function() {
                currentToken.forceUpdate = true;
                newVFRequestDelay += VFTokenRequestDelay;
                _getVirtueFusionToken();
            }, newVFRequestDelay);

            currentToken.request.reject(data);
        };

        if (_isTokenFresh() && !currentToken.forceUpdate) {
            currentToken.token && currentToken.request.resolve(currentToken.token);
        } else {
            authManager.getTemporaryAuthenticationToken(function(tempToken) {
                currentToken.forceUpdate = false;
                var options = {
                    'umsTemporaryToken': tempToken,
                    'description': 'Get Virtue Fusion temp token',
                    'ID': 89501,
                    'responses': [89502, 89521]
                };

                server
                    .send(options)
                    .done(onSuccess)
                    .fail(onError);
            });
        }

        return currentToken;
    };

    /**
     * Main Virtue Fusion method for requests
     * @private
     * @params {Object} options from public methods
     * @return {Deferred}
     * */
    var _doRequest = function(options) {
        var request = $.Deferred();

        _getVirtueFusionToken().request
            .done(function(virtueFusionToken) {
                server
                        .send(_.extend({}, options, {vfToken: virtueFusionToken}))
                        .done(function(data) { request.resolve(options, data); })
                        .fail(function(data) { request.reject(data); });
            })
            .fail(request.reject);

        return request.promise();
    };

    var _requestWrapper = function(options) {
        var wrappedRequest = $.Deferred();
        var key = JSON.stringify(options);

        if (!requests[key] || requests[key].state() !== 'pending') {
            requests[key] = _doRequest(options);
        }

        requests[key]
            .done(function(opts, data) {
                delete requests[key];

                wrappedRequest.resolve(data);
            })
            .fail(function(data) {
                wrappedRequest.reject(data);
            });

        return wrappedRequest.promise();
    };

    module.exports = {

        /**
         * Get Virtue Fusion loyalty points
         * @public
         * @return {Deferred}
         * */
        getLoyaltyInfo: function() {
            return _requestWrapper({
                'description': 'Virtue Fusion get loyalty points info',
                'ID': 89509,
                'responses': [89510, 89525]
            });
        },

        /**
         * Get Virtue Fusion balance
         * @public
         * @param  {Object} options {balanceType: string},
         * @return {Deferred}
         * */
        getBalance: function(options) {
            return _requestWrapper({
                'description': 'Get Virtue Fusion balance',
                'balanceType': options.balanceType,
                'correlationId': options.correlationId,
                'ID': 89503,
                'responses': [89504, 89522]
            });
        },

        /**
         * Get Virtue Fusion balances [list of balances]
         * @public
         * @return {Deferred}
         * */
        getBalances: function() {
            return _requestWrapper({
                'description': 'Get Virtue Fusion balances list',
                'ID': 89505,
                'responses': [89506, 89500]
            });
        },

        /**
         * Virtue Fusion register promo code method
         * @public
         * @param  {Object} options {promoCode: string},
         * @return {Deferred}
         * */
        registerPromoCode: function(options) {
            return _requestWrapper({
                'promoCode': options.promoCode,
                'description': 'Virtue Fusion register promo code',
                'ID': 89507,
                'responses':[89508, 89524]
            });
        },

        /**
         * Virtue Fusion redeem loyalty points method
         * @public
         * @param  {Object} options {amount: Float, balanceType: string},
         * @return {Deferred}
         * */
        redeemLoyaltyPoints: function(options) {
            return _requestWrapper({
                'balanceType': options.balanceType,
                'amount': options.amount,
                'description': 'Virtue Fusion redeem loyalty points',
                'ID': 89511,
                'responses': [89512, 89526]
            });
        },

        /**
         * Virtue Fusion set players timeout method
         * @public
         * @param  {Object} options {startTime: Date, period: Integer},
         * @return {Deferred}
         * */
        setPlayerTimeout: function(options) {
            return _requestWrapper({
                'startTime': options.startTime,
                'description': 'Set Virtue Fusion player timeout',
                'period': options.period || 3600,
                'emailNotificationType': ['START'],
                correlationId: 'ignore-limits-notification',
                'ID': 89910
            });
        },

        /**
         * get Virtue Fusion player reality check
         * @public
         * @return {Deferred}
         * */
        getPlayerRealityCheck: function() {
            return _requestWrapper({
                'ID': 89913,
                'description': 'Get Virtue Fusion player reality check'
            });
        }
    };
});
